import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import type { UpdateOrgSettings } from '@sdk/authentication/types/OrganisationType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { LaborCostCalculatorType } from '@sdk/common/types';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import Text14 from '@sdk/components/ui/typography/Text14';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import Column from '@sdk/components/ui/spacing/Column';
import VisibleHoursPicker from '@sdk/components/forms/VisibleHoursPicker';
import { getCutoffTime } from '@sdk/components/forms/visibleHours.utils';
import View from '@sdk/components/primitives/View';
import validateTime from 'sling-sdk/src/utils/validation/validateTime';

import { TabType } from '../types';
import { useSettingsErrorsContext } from '../SettingsErrorContext';
import { FIELD_WIDTH_205 } from '../constants';

import SettingsSection from './SettingsSection';
import RowSpaceBetween from './RowSpaceBetween';
import SettingsSwitcherRow from './SettingsSwitcherRow';

const VisibleHoursContainer = styled(View)`
  width: ${FIELD_WIDTH_205}px;
`;

type Props = {
  visibleHoursStart: string;
  visibleHoursEnd: string;
  enableShiftCostSplitting: boolean;
  onUserOrgSettingsChange: UpdateOrgSettings;
  usTimeFormat: boolean;
  laborCostCalculatorType: LaborCostCalculatorType;
};

const HoursOfOperation: React.FC<React.PropsWithChildren<Props>> = ({
  visibleHoursStart,
  visibleHoursEnd,
  enableShiftCostSplitting,
  onUserOrgSettingsChange,
  usTimeFormat,
  laborCostCalculatorType,
}) => {
  const { currentUser } = useAuth();
  const { toggleError, isFieldValid } = useSettingsErrorsContext();
  const [cutoffTime, setCutoffTime] = useState(
    getCutoffTime(visibleHoursStart, visibleHoursEnd, usTimeFormat)
  );

  useEffect(() => {
    setCutoffTime(getCutoffTime(visibleHoursStart, visibleHoursEnd, usTimeFormat));
  }, [usTimeFormat, visibleHoursEnd, visibleHoursStart]);

  return (
    <>
      <SettingsSection>
        <Column pBottom={20}>
          <RowSpaceBetween>
            <Column>
              <Text14 semiBold themeColor={ThemeColorType.gray}>
                {gettext('Hours of operation')}
              </Text14>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('The day view and unavailability will be limited to these hours.')}
              </Text14>
            </Column>
            <VisibleHoursContainer>
              <VisibleHoursPicker
                disabled={!currentUser.isAdmin}
                visibleHoursStart={visibleHoursStart}
                visibleHoursEnd={visibleHoursEnd}
                usTimeFormat={usTimeFormat}
                onChange={({
                  startHour,
                  endHour,
                }: {
                  startHour: string;
                  endHour: string;
                }): void => {
                  onUserOrgSettingsChange({
                    visibleHoursStart: startHour,
                    visibleHoursEnd: endHour,
                  });
                  toggleError(TabType.schedule, 'startHour', !validateTime(startHour));
                  toggleError(TabType.schedule, 'endHour', !validateTime(endHour));
                }}
                startHourInvalid={!isFieldValid('startHour')}
                endHourInvalid={!isFieldValid('endHour')}
              />
            </VisibleHoursContainer>
          </RowSpaceBetween>
        </Column>
      </SettingsSection>

      {laborCostCalculatorType === LaborCostCalculatorType.us ? (
        <SettingsSwitcherRow
          disabled={!currentUser.isAdmin}
          withSeparator
          value={enableShiftCostSplitting}
          onChange={(value): void => onUserOrgSettingsChange({ enableShiftCostSplitting: value })}
          title={gettext('Cutoff time for overnight hours')}
          subtitleItem={
            <Text14 themeColor={ThemeColorType.gray}>
              <TranslateWithComponents
                replaceComponents={{
                  component: (
                    <Text14 themeColor={ThemeColorType.primary}>{cutoffTime || 'X'}</Text14>
                  ),
                }}>
                {gettext(
                  'When enabled, hours that cross [[component]] will be split between days on schedule and payroll.'
                )}
              </TranslateWithComponents>
            </Text14>
          }
        />
      ) : null}
    </>
  );
};

export default HoursOfOperation;
